import React, { useEffect, useRef, useState } from "react";
import classes from "./settings.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
  getProfile,
  upsertProfile,
} from "../../../../redux/slices/artistProfileSlice";
import { getProfilePictue, uploadProfilePicture } from "../../../../services";
import { useActionData } from "react-router-dom";
import Cookies from "js-cookie";
import useAuth from "../../../../hooks/useAuth";
import { getProfilePicture } from "../../../../redux/slices/artistSlice";

const Settings = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState(null);
  const [formValues, setFormValues] = useState({});
  const { artistProfile } = useSelector((state) => state.artistProfile);

  const [profile, setProfile] = useState({
    name: `${artistProfile?.first_name} ${artistProfile?.last_name}`,
    username: artistProfile?.username,
    email: artistProfile?.email,
    phone: artistProfile?.phone_number,
    password: "••••••••",
    profileUrl: "",
  });

  const [passwordVisible, setPasswordVisible] = useState(false);

  useEffect(() => {
    dispatch(getProfile());
    if (user?.username) {
      dispatch(getProfilePicture(user?.username));
    }
  }, [user?.username]);

  useEffect(() => {
    setProfile({
      name: `${artistProfile?.first_name} ${artistProfile?.last_name}`,
      username: artistProfile?.username,
      email: artistProfile?.email,
      phone: artistProfile?.phone_number,
      password: "••••••••",
      profileUrl: "",
    });
  }, [fileInputRef]);

  const handleEdit = (section, initialValue) => {
    setActiveSection(section);
    setFormValues({ [section]: initialValue });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleSocialChange = (platform, value) => {
    setFormValues({
      ...formValues,
      socialLinks: {
        ...formValues.socialLinks,
        [platform]: value,
      },
    });
  };

  const handleSave = () => {
    if (activeSection === "socialLinks") {
      setProfile({
        ...profile,
        socialLinks: {
          ...profile.socialLinks,
          ...formValues.socialLinks,
        },
      });
      dispatch(
        upsertProfile({
          socialLinks: {
            ...profile.socialLinks,
            ...formValues.socialLinks,
          },
        })
      );
    } else {
      setProfile({
        ...profile,
        [activeSection]: formValues[activeSection],
      });
    }

    setActiveSection(null);
  };

  const handleCancel = () => {
    setActiveSection(null);
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleButtonClick = () => {
    fileInputRef.current.click(); // triggers the hidden file input
  };

  const handleFileChange = async (event) => {
    const localFile = await event.target.files[0];

    if (localFile) {
      const formData = new FormData();
      formData.append("media", localFile);

      const response = await fetch(uploadProfilePicture, {
        method: "POST",
        body: formData,
        headers: {
          authorization: `Bearer ${Cookies.get("accessToken")}`,
        },
      });
    }

    dispatch(getProfile());

    // setFormData((prev) => ({ ...prev, ["photo"]: event.target.files[0] }));
  };

  // Render a regular field (for name, email, etc.)
  const renderField = (label, key, value, type = "text") => {
    let isEditing = activeSection === key;

    return (
      <div className={classes.field}>
        <div className={classes.fieldHeader}>
          <label className={classes.fieldLabel}>{label}</label>
          {!isEditing && [""].includes(key) && (
            <button
              onClick={() => handleEdit(key, value)}
              className={classes.editButton}
            >
              Edit
            </button>
          )}
        </div>

        {isEditing ? (
          <div className={classes.editContainer}>
            <input
              type={
                key === "password"
                  ? passwordVisible
                    ? "text"
                    : "password"
                  : type
              }
              name={key}
              value={formValues[key]}
              onChange={handleChange}
              className={classes.input}
            />
            {key === "password" && (
              <button
                onClick={togglePasswordVisibility}
                className={classes.togglePassword}
                type="button"
              >
                {passwordVisible ? "Hide" : "Show"}
              </button>
            )}
            <div className={classes.actionButtons}>
              <button onClick={handleSave} className={classes.saveButton}>
                Save
              </button>
              <button onClick={handleCancel} className={classes.cancelButton}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className={classes.fieldValue}>
            {key === "password" ? "••••••••" : value}
          </div>
        )}
      </div>
    );
  };

  // Render social media links section
  const renderSocialLinks = () => {
    const isEditing = activeSection === "socialLinks";
    const socialPlatforms = [
      { key: "twitter", label: "Twitter", icon: "🐦" },
      { key: "instagram", label: "Instagram", icon: "📸" },
      { key: "twitch", label: "Twitch", icon: "🎮" },
      { key: "spotify", label: "Spotify", icon: "🎵" },
    ];

    return (
      <div className={classes.field}>
        <div className={classes.fieldHeader}>
          <label className={classes.fieldLabel}>Social Media</label>
          {!isEditing && (
            <button
              onClick={() => {
                handleEdit("socialLinks", profile.socialLinks);
                setFormValues({ socialLinks: { ...profile.socialLinks } });
              }}
              className={classes.editButton}
            >
              Edit
            </button>
          )}
        </div>

        {isEditing ? (
          <div className={classes.editContainer}>
            {socialPlatforms.map((platform) => (
              <div key={platform.key} className={classes.socialInputGroup}>
                <label className={classes.socialLabel}>
                  {platform.icon} {platform.label}
                </label>
                <input
                  type="text"
                  value={formValues.socialLinks[platform.key] || ""}
                  onChange={(e) =>
                    handleSocialChange(platform.key, e.target.value)
                  }
                  className={classes.input}
                  placeholder={`Enter your ${platform.label} URL`}
                />
              </div>
            ))}

            <div className={classes.actionButtons}>
              <button onClick={handleSave} className={classes.saveButton}>
                Save
              </button>
              <button onClick={handleCancel} className={classes.cancelButton}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className={classes.socialLinksDisplay}>
            {socialPlatforms.map((platform) => (
              <div key={platform.key} className={classes.socialLink}>
                <span className={classes.socialIcon}>{platform.icon}</span>
                <a
                  href={profile.socialLinks[platform.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.socialUrl}
                >
                  {profile.socialLinks[platform.key]
                    ? profile.socialLinks[platform.key].replace(
                        /^https?:\/\/(www\.)?/,
                        ""
                      )
                    : "Not set"}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render bio field (textarea)
  const renderBio = () => {
    const isEditing = activeSection === "bio";

    return (
      <div className={classes.field}>
        <div className={classes.fieldHeader}>
          <label className={classes.fieldLabel}>Bio</label>
          {!isEditing && (
            <button
              onClick={() => handleEdit("bio", profile.bio)}
              className={classes.editButton}
            >
              Edit
            </button>
          )}
        </div>

        {isEditing ? (
          <div className={classes.editContainer}>
            <textarea
              name="bio"
              value={formValues.bio}
              onChange={handleChange}
              className={classes.textarea}
              rows={4}
            />
            <div className={classes.actionButtons}>
              <button onClick={handleSave} className={classes.saveButton}>
                Save
              </button>
              <button onClick={handleCancel} className={classes.cancelButton}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className={classes.fieldValue}>{profile.bio}</div>
        )}
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h1 className={classes.title}>Edit Profile</h1>
        <div className={classes.profileImageContainer}>
          <img
            src={artistProfile?.displayPictureUrl}
            alt="Profile"
            className={classes.profileImage}
          />
          <button
            onClick={handleButtonClick}
            className={classes.changePhotoButton}
          >
            Change Photo
          </button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={(e) => handleFileChange(e)}
            style={{ display: "none" }} // keep it hidden
          />
        </div>
      </div>

      <div className={classes.profileForm}>
        {renderField(
          "Name",
          "name",
          `${artistProfile?.first_name} ${artistProfile?.last_name}`
        )}
        {renderField("Username", "username", artistProfile?.username)}
        {renderField("Email", "email", artistProfile?.email, "email")}
        {renderField("Phone", "phone", artistProfile?.phone_number, "tel")}
        {renderField("Password", "password", profile.password, "password")}

        {/* {renderBio()} */}
        {/* {renderSocialLinks()} */}
      </div>
    </div>
  );
};

export default Settings;
